import { ORDER_KPI_KEYS, ORDER_KPIS, type OrderKpiKey } from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { KPI_TEXT } from "@/components/OrderKpiStrip";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

/**
 * Logiful intern → Kennzahlen: welche Kacheln Kunden über der Auftragsliste sehen (für alle Kunden).
 * Heikle Leistungswerte sind gekennzeichnet; was hier aus ist, berechnet der Server gar nicht erst.
 */
export function AdminKpisPage() {
  const qc = useQueryClient();
  const { me } = useTenant();
  const canEdit = me.user.staffRole === "staff_admin";
  const q = useQuery({ queryKey: ["admin-order-kpis"], queryFn: () => api.admin.orderKpis() });
  const [enabled, setEnabled] = useState<OrderKpiKey[] | null>(null);
  useEffect(() => { if (q.data && !enabled) setEnabled(q.data.enabled); }, [q.data, enabled]);
  const save = useMutation({
    mutationFn: () => api.admin.saveOrderKpis({ enabled: enabled! }),
    onSuccess: (r) => {
      toast.success(t("Gespeichert"), { description: t("Kunden sehen die Kennzahlen ab sofort so.") });
      setEnabled(r.enabled);
      void qc.invalidateQueries({ queryKey: ["admin-order-kpis"] });
      void qc.invalidateQueries({ queryKey: ["order-kpis"] });
    },
    onError: () => toast.error(t("Speichern fehlgeschlagen")),
  });

  if (!enabled) return <div className="px-4 md:px-8"><Skeleton className="h-64 w-full" /></div>;
  const dirty = JSON.stringify(enabled) !== JSON.stringify(q.data?.enabled);
  const off = ORDER_KPI_KEYS.filter((k) => !enabled.includes(k));
  const move = (i: number, d: -1 | 1) => setEnabled((xs) => { const c = [...xs!]; [c[i], c[i + d]] = [c[i + d]!, c[i]!]; return c; });

  const Row = ({ k, i }: { k: OrderKpiKey; i: number | null }) => (
    <li className="flex items-center gap-3 px-4 py-3">
      <button role="switch" aria-checked={i != null} disabled={!canEdit} aria-label={t(KPI_TEXT[k].label)} onClick={() => setEnabled((xs) => (i != null ? xs!.filter((x) => x !== k) : [...xs!, k]))} className="disabled:opacity-50">
        <span className={cx("block w-8 h-[18px] rounded-full p-0.5 transition-colors", i != null ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", i != null && "translate-x-3.5")} /></span>
      </button>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[13.5px] font-medium">
          {t(KPI_TEXT[k].label)}
          {ORDER_KPIS[k].sensitive && <Badge tone="warning" icon={<ShieldAlert className="size-3" />}>{t("heikel")}</Badge>}
        </span>
        <span className="block text-[12.5px] text-fg-muted">{t(KPI_TEXT[k].hint)}</span>
      </span>
      {i != null && canEdit && (
        <span className="flex">
          <button disabled={i === 0} onClick={() => move(i, -1)} className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted disabled:opacity-30" aria-label={t("Nach oben")}><ArrowUp className="size-4" /></button>
          <button disabled={i === enabled.length - 1} onClick={() => move(i, 1)} className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted disabled:opacity-30" aria-label={t("Nach unten")}><ArrowDown className="size-4" /></button>
        </span>
      )}
    </li>
  );

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Kennzahlen")}
        description={t("Welche Kacheln Kunden über der Auftragsliste sehen – für alle Kunden. Abgeschaltete Kennzahlen werden nicht berechnet und verlassen den Server nie.")}
        actions={canEdit && dirty ? <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button> : undefined}
      />
      <div className="px-4 md:px-8 max-w-3xl space-y-4">
        <Card>
          <h2 className="px-4 pt-4 font-semibold text-[14px]">{t("Angezeigt (in dieser Reihenfolge)")}</h2>
          {enabled.length ? <ul className="divide-y divide-border">{enabled.map((k, i) => <Row key={k} k={k} i={i} />)}</ul> : <p className="px-4 py-4 text-[13px] text-fg-muted">{t("Keine – die Leiste ist für Kunden ausgeblendet.")}</p>}
        </Card>
        {!!off.length && (
          <Card>
            <h2 className="px-4 pt-4 font-semibold text-[14px]">{t("Nicht angezeigt")}</h2>
            <ul className="divide-y divide-border">{off.map((k) => <Row key={k} k={k} i={null} />)}</ul>
          </Card>
        )}
        <p className="text-[12.5px] text-fg-muted flex gap-2"><ShieldAlert className="size-4 shrink-0 text-warning" />{t("„Heikel“: Leistungswerte von Logiful bzw. dem Dienstleister. Einmal sichtbar, werden sie zum Maßstab, an dem Kunden uns messen – nur einschalten, wenn wir die Werte verlässlich halten.")}</p>
        {!canEdit && <p className="text-[12.5px] text-fg-muted">{t("Ändern dürfen nur Logiful-Admins.")}</p>}
      </div>
    </div>
  );
}
